import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core'
import { Observable, Subject, Subscription } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'

/** One row of the list. Kept structural rather than tied to ClientRoleMember so the vehicle and
 *  HME drawers — whose members come from their own services — can feed the same component.
 *
 *  No `code`: the pass code is the scan credential and is not fetched by the portal at all. The
 *  secondary line shows the phone number instead, which is what identifies a person here. */
export interface RoleMemberRow {
  id: number | string
  name?: string | null
  last_name?: string | null
  mobile?: string | null
  vehicle_number?: string | null
  allowed?: number | null
}

/** How the host fetches a page. Every drawer has its own source, so the component asks for one. */
export type RoleMembersFetch = (params: { search: string; skip: number; take: number }) => Observable<{ data: RoleMemberRow[]; total: number }>

/**
 * The list of people (or vehicles, or equipment) carrying a role, as shown at the bottom of every
 * role drawer — YB-2073.
 *
 * It replaces four copies of a bare `@for` over a fixed-height card that printed a first name and
 * nothing else, had no paging, and was clipped to `calc(100vh - 850px)` — a height that goes
 * NEGATIVE on a laptop screen, which is why the box usually looked empty.
 *
 * Paging and search are server-side by design: a role can hold tens of thousands of passes, and
 * the whole point of the query behind this is to stop shipping all of them to draw a list.
 */
@Component({
  selector: 'app-role-members',
  templateUrl: './role-members.component.html',
  styleUrls: ['./role-members.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleMembersComponent implements OnChanges, OnDestroy {
  /** Section heading, a translate key (e.g. 'AUTHORIZED.PAGE_TITLE'). */
  @Input() title: string = ''

  /**
   * Page loader. Setting it (re)loads page 1 — hosts assign it when the drawer opens, so a
   * different role never shows the previous role's members while its own page is in flight.
   * Null means "nothing to show yet" (a role being created has no members).
   */
  @Input() fetch: RoleMembersFetch | null = null

  /** Rows per page. 10 keeps the list inside the drawer without its own inner scrollbar. */
  @Input() rows: number = 10

  /**
   * How to label a row. Defaults to first + last name; the vehicle and HME drawers pass their own,
   * because what identifies a row there (licence plate, equipment id) lives inside
   * `type_specific_data` rather than in a name column.
   */
  @Input() labelFn?: (row: RoleMemberRow) => string

  /** A row was clicked — hosts that can open the underlying pass listen to this. */
  @Output() rowClick: EventEmitter<RoleMemberRow> = new EventEmitter<RoleMemberRow>()

  members: RoleMemberRow[] = []
  total: number = 0
  first: number = 0
  loading: boolean = false
  search: string = ''

  /** Typing must not fire a request per keystroke; 300ms is the usual portal debounce. */
  private readonly searchInput = new Subject<string>()
  private readonly searchSubscription: Subscription
  private pageSubscription?: Subscription

  constructor(private cdr: ChangeDetectorRef) {
    this.searchSubscription = this.searchInput.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value) => {
      this.search = value
      this.first = 0
      this.load()
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fetch']) {
      this.search = ''
      this.first = 0
      this.members = []
      this.total = 0
      this.load()
    }
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe()
    this.pageSubscription?.unsubscribe()
  }

  onSearch(value: string) {
    this.searchInput.next((value ?? '').trim())
  }

  onPage(event: { first?: number; rows?: number }) {
    this.first = event.first ?? 0
    this.load()
  }

  /** Full name, falling back to whatever else identifies the row so it is never blank. */
  displayName(member: RoleMemberRow): string {
    if (this.labelFn) return this.labelFn(member)
    const name = `${member.name ?? ''} ${member.last_name ?? ''}`.trim()
    return name || member.vehicle_number || member.mobile || `#${member.id}`
  }

  /** The line under/beside the name. Suppressed when it is already what the name shows. */
  displayDetail(member: RoleMemberRow): string {
    const detail = member.mobile ?? ''
    return detail === this.displayName(member) ? '' : detail
  }

  initials(member: RoleMemberRow): string {
    const name = this.displayName(member)
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
  }

  /** `allowed` is a count of remaining uses, so 0 is a real, revoked pass — not "unset". */
  isActive(member: RoleMemberRow): boolean {
    return Number(member.allowed ?? 0) > 0
  }

  private load() {
    if (!this.fetch) {
      this.members = []
      this.total = 0
      this.loading = false
      this.cdr.markForCheck()
      return
    }

    // Drop the in-flight page: typing produces overlapping requests and the last one asked for
    // is the one the admin is waiting to see, whatever order they come back in.
    this.pageSubscription?.unsubscribe()
    this.loading = true
    this.cdr.markForCheck()

    this.pageSubscription = this.fetch({ search: this.search, skip: this.first, take: this.rows }).subscribe({
      next: (page) => {
        this.members = page?.data ?? []
        this.total = page?.total ?? 0
        this.loading = false
        this.cdr.markForCheck()
      },
      error: () => {
        this.members = []
        this.total = 0
        this.loading = false
        this.cdr.markForCheck()
      },
    })
  }
}
